/* <tsa-accordion [data-single-open]>
   Progressive enhancement over native <details>: the markup works with no JS
   at all, this only adds height easing and optional single-open behaviour.
   Wrapped in an IIFE so nothing lands in global scope. */
(function () {
  const DURATION = 220;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class TsaAccordion extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('details.tsa-accordion__item'));
      if (!this.items.length) return;
      this.singleOpen = this.hasAttribute('data-single-open');
      this.onSummaryClick = this.onSummaryClick.bind(this);
      this.items.forEach((item) => {
        const summary = item.querySelector('summary');
        if (summary) summary.addEventListener('click', this.onSummaryClick);
      });
    }

    disconnectedCallback() {
      if (!this.items) return;
      this.items.forEach((item) => {
        const summary = item.querySelector('summary');
        if (summary) summary.removeEventListener('click', this.onSummaryClick);
      });
    }

    onSummaryClick(event) {
      if (prefersReducedMotion()) return; /* let the browser do it instantly */
      const item = event.currentTarget.closest('details');
      const panel = item.querySelector('.tsa-accordion__panel');
      if (!panel) return;

      event.preventDefault();

      /* `item.open` stays true until a collapse finishes, so a click landing
         mid-collapse would read as "open" and collapse again, making a
         just-closed panel take two clicks to reopen. Treat collapsing as
         already closed. */
      const isOpen = item.open && item.dataset.collapsing !== '1';

      if (isOpen) {
        this.collapse(item, panel);
      } else {
        if (this.singleOpen) {
          this.items.forEach((other) => {
            if (other !== item && other.open && other.dataset.collapsing !== '1') {
              this.collapse(other, other.querySelector('.tsa-accordion__panel'));
            }
          });
        }
        this.expand(item, panel);
      }
    }

    /* One timer per panel, cleared on re-entry. Without this, clicking a
       summary twice inside DURATION lets the first click's timer fire and
       clear the inline height, so the panel visibly re-expands to full
       height before the second timer closes it again. */
    setTimer(panel, fn) {
      this.clearTimer(panel);
      panel.tsaTimer = window.setTimeout(fn, DURATION);
    }

    clearTimer(panel) {
      if (panel.tsaTimer) {
        window.clearTimeout(panel.tsaTimer);
        panel.tsaTimer = null;
      }
    }

    expand(item, panel) {
      this.clearTimer(panel);
      delete item.dataset.collapsing;
      item.open = true;
      const target = panel.scrollHeight;
      /* Write the start height with transitions off and force a reflow, so
         the browser's last computed value really is 0px. Writing 0px and the
         target in the same frame leaves the computed start at `auto`, which
         is not interpolatable, so nothing animates and the panel snaps. */
      panel.style.transition = 'none';
      panel.style.height = '0px';
      void panel.offsetHeight;
      panel.style.transition = 'height ' + DURATION + 'ms';
      panel.style.height = target + 'px';
      this.setTimer(panel, () => {
        panel.style.height = '';
        panel.style.transition = '';
      });
    }

    collapse(item, panel) {
      if (!panel) { item.open = false; return; }
      this.clearTimer(panel);
      item.dataset.collapsing = '1';
      panel.style.transition = 'none';
      panel.style.height = panel.scrollHeight + 'px';
      void panel.offsetHeight;
      panel.style.transition = 'height ' + DURATION + 'ms';
      panel.style.height = '0px';
      this.setTimer(panel, () => {
        item.open = false;
        delete item.dataset.collapsing;
        panel.style.height = '';
        panel.style.transition = '';
      });
    }
  }

  customElements.define('tsa-accordion', TsaAccordion);
})();
